import { useEffect, useMemo, useState } from 'react';
import type { SignatureStrokes } from '../../domain/signature';
import { signatureIsMeaningful } from '../../domain/signature';
import { useT, type MessageKey } from '../../i18n/i18n';
import { labelWithTag } from '../../i18n/labels';
import { getRemoteSignTransport } from '../../backend/remoteSignClient';
import type { RemoteSignFacts } from '../../../supabase/functions/_shared/remoteSignProtocol';
import { Button, Card, CheckRow, Field, InlineMessage, SignaturePad, TextInput } from '../../ui/components';

// The client's signing page (Owner decision list 2026-10-19 items 4 / 45): opened from the inspector's one-time
// link at /sign/<token>, with NO account. It shows the whole agreement exactly as the inspector's device rendered
// it (a sandboxed frame: the document is shown, never run), the five facts, the two consents — electronic records
// and signatures (required, 15 U.S.C. §7001(c)) and cloud storage + AI-assisted analysis (voluntary) — each timed
// when ticked, "I have read and agree", the typed name and the hand-drawn signature. Every message sits under the
// field it is about (Owner rule 9).
type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; documentHtml: string; facts: RemoteSignFacts }
  | { kind: 'done' }
  | { kind: 'closed'; reason: 'signed' | 'expired' | 'notFound' | 'unavailable' };

const CLOSED_KEYS: Record<Extract<PageState, { kind: 'closed' }>['reason'], MessageKey> = {
  signed: 'remotePage.alreadySigned',
  expired: 'remotePage.expired',
  notFound: 'remotePage.notFound',
  unavailable: 'remotePage.unavailable'
};

export function RemoteSignPage(props: { token: string }) {
  const { t, locale } = useT();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [signature, setSignature] = useState<SignatureStrokes | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [esign, setEsign] = useState<number | null>(null);
  const [ai, setAi] = useState<number | null>(null);
  // Agreement copy of 2026-10-28: the limitation of liability and claims terms, accepted by the client (required).
  const [liability, setLiability] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<MessageKey | null>(null);

  useEffect(() => {
    let alive = true;
    void getRemoteSignTransport()
      .call({ action: 'open', token: props.token }, null)
      .then((outcome) => {
        if (!alive) return;
        if (outcome.kind === 'failed') {
          setState({ kind: 'closed', reason: outcome.code === 'expired' ? 'expired' : outcome.code === 'notFound' ? 'notFound' : 'unavailable' });
          return;
        }
        if (outcome.response.action !== 'open') return setState({ kind: 'closed', reason: 'unavailable' });
        if (outcome.response.state !== 'waiting') return setState({ kind: 'closed', reason: outcome.response.state === 'signed' ? 'signed' : 'expired' });
        setName(outcome.response.facts.client);
        setState({ kind: 'ready', documentHtml: outcome.response.documentHtml, facts: outcome.response.facts });
      });
    return () => {
      alive = false;
    };
  }, [props.token]);

  const frame = useMemo(
    () => (state.kind === 'ready' ? `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body{margin:12px;background:#fff}</style></head><body>${state.documentHtml}</body></html>` : ''),
    [state]
  );

  const nameMissing = name.trim().length === 0;
  const signatureMissing = !signatureIsMeaningful(signature);
  // Only a document that carries the terms asks for them (a link made before the copy of 2026-10-28 does not).
  const asksLiability = state.kind === 'ready' && state.documentHtml.includes('data-claims=');

  async function submit() {
    setTried(true);
    setSubmitError(null);
    if (nameMissing || signatureMissing || !accepted || esign === null || (asksLiability && liability === null) || !signature) return;
    setBusy(true);
    try {
      const outcome = await getRemoteSignTransport().call(
        { action: 'sign', token: props.token, submission: { name: name.trim(), signature, accepted: true, esignConsent: true, esignConsentAt: esign, aiConsent: ai !== null, aiConsentAt: ai, liabilityAckAt: asksLiability ? liability : null } },
        null
      );
      if (outcome.kind === 'ok') setState({ kind: 'done' });
      else if (outcome.code === 'alreadySigned') setState({ kind: 'closed', reason: 'signed' });
      else if (outcome.code === 'expired') setState({ kind: 'closed', reason: 'expired' });
      else setSubmitError(outcome.code === 'offline' || outcome.code === 'network' ? 'remotePage.offline' : 'remotePage.unavailable');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="remote-page" data-testid="remote-sign-page">
      {/* The page speaks the client's browser language (the agreement is in the language chosen for the client). */}
      <header className="remote-page-head">
        <p className="auth-brand">{t('shell.productName')}</p>
      </header>
      <h1 className="remote-page-title">{t('remotePage.title')}</h1>

      {state.kind === 'loading' ? <p className="card-note">{t('shell.loading')}</p> : null}
      {state.kind === 'closed' ? <InlineMessage kind={state.reason === 'signed' ? 'ok' : 'err'} text={t(CLOSED_KEYS[state.reason])} /> : null}
      {state.kind === 'done' ? (
        <Card testId="remote-page-done">
          <InlineMessage kind="ok" text={t('remotePage.done')} />
          <p className="card-note">{t('remotePage.doneNext')}</p>
        </Card>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <p className="card-note">{t('remotePage.intro')}</p>
          <Card testId="remote-page-facts">
            <div>
              {(
                [
                  ['agreement.docProperty', state.facts.property],
                  ['agreement.docClient', state.facts.client],
                  ['agreement.docInspector', state.facts.inspector],
                  ['agreement.docInspectionDate', state.facts.inspectionDate],
                  ['agreement.docFee', state.facts.fee],
                  ['agreement.docVersion', state.facts.textVersion]
                ] as Array<[MessageKey, string]>
              ).map(([key, value]) => (
                <div className="kv" key={key}>
                  <span className="kv-key">{t(key)}</span>
                  <span className="kv-val">{value}</span>
                </div>
              ))}
            </div>
          </Card>
          <iframe className="remote-page-document" title={t('remotePage.documentTitle')} srcDoc={frame} sandbox="" data-testid="remote-page-document" />
          <Card testId="remote-page-form">
            <div className="form-stack">
              <CheckRow label={labelWithTag(locale, t('agreement.esignTick'), t('common.required'))} checked={esign !== null} onChange={(on) => setEsign(on ? Date.now() : null)} testId="remote-page-esign" />
              {tried && esign === null ? <InlineMessage kind="err" text={t('remotePage.needEsign')} /> : null}
              {asksLiability ? <CheckRow label={labelWithTag(locale, t('agreement.liabilityTick'), t('common.required'))} checked={liability !== null} onChange={(on) => setLiability(on ? Date.now() : null)} testId="remote-page-liability" /> : null}
              {asksLiability && tried && liability === null ? <InlineMessage kind="err" text={t('remotePage.needLiability')} /> : null}
              <CheckRow label={labelWithTag(locale, t('agreement.aiTick'), t('common.optional'))} checked={ai !== null} onChange={(on) => setAi(on ? Date.now() : null)} testId="remote-page-ai" />
              <p className="card-note">{t('remotePage.consentsNote')}</p>
              <Field label={labelWithTag(locale, t('agreement.signerName'), t('common.required'))} htmlFor="remote-page-name">
                <TextInput id="remote-page-name" value={name} onChange={setName} placeholder={t('agreement.signerNamePlaceholder')} testId="remote-page-name" />
              </Field>
              {tried && nameMissing ? <InlineMessage kind="err" text={t('remotePage.needName')} /> : null}
              <SignaturePad value={signature} onChange={setSignature} clearLabel={t('agreement.clear')} hint={t('agreement.drawHere')} testId="remote-page-pad" />
              {tried && signatureMissing ? <InlineMessage kind="err" text={t('remotePage.needSignature')} /> : null}
              <CheckRow label={t('agreement.acceptClient')} checked={accepted} onChange={setAccepted} testId="remote-page-accept" />
              {tried && !accepted ? <InlineMessage kind="err" text={t('remotePage.needAccept')} /> : null}
              {submitError ? <InlineMessage kind="err" text={t(submitError)} /> : null}
              <Button icon="draw" onClick={() => void submit()} disabled={busy} testId="remote-page-submit">
                {busy ? t('remotePage.sending') : t('remotePage.submit')}
              </Button>
              <p className="card-note">{t('remotePage.privacy')}</p>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
